import { Fragment, useEffect, useRef, useState } from "react";
import {
	HelmetDexteel,
	TimeService,
	useHasPermission,
} from "@dexteel/mesf-core";
import { ExpandLess, ExpandMore, PlayArrow, Stop } from "@mui/icons-material";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	Collapse,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment-timezone";
import {
	COMPARISON_PERMISSION,
	DIFFERENCE_TYPES,
	conceptStatus,
	type ComparisonConcept,
	type ConceptRun,
	type ReconciliationSelection,
} from "./models";
import { compareConcept, getConcepts } from "./repository";
import { DifferenceDetails } from "./DifferenceDetails";
import { createComparisonRunId } from "./runId";
import { ReconciliationDialog } from "./ReconciliationDialog";

function ConceptStatusChip({ row }: { row: ConceptRun }) {
	const status = conceptStatus(row);
	return (
		<Tooltip title={status.description}>
			<Chip size="small" label={status.label} color={status.color} />
		</Tooltip>
	);
}

export function RollshopComparisonPage() {
	const hasPermission = useHasPermission();
	return (
		<>
			<HelmetDexteel title="Rollshop vs MES" />
			{hasPermission(COMPARISON_PERMISSION) ? (
				<ComparisonReport />
			) : (
				<Alert severity="warning">
					This report is available to administrators only.
				</Alert>
			)}
		</>
	);
}

export function ComparisonReport() {
	const timezone = TimeService.getInstance().getServerTimeZone();
	const [concepts, setConcepts] = useState<ComparisonConcept[]>([]);
	const [selected, setSelected] = useState<ComparisonConcept[]>([]);
	const [from, setFrom] = useState<Moment | null>(() =>
		moment().tz(timezone).subtract(7, "days").milliseconds(0),
	);
	const [to, setTo] = useState<Moment | null>(() =>
		moment().tz(timezone).milliseconds(0),
	);
	const [loadError, setLoadError] = useState("");
	const [retry, setRetry] = useState(0);
	const [running, setRunning] = useState(false);
	const [refreshing, setRefreshing] = useState<string | null>(null);
	const [run, setRun] = useState<{
		id: string;
		from: string;
		to: string;
		started: number;
	} | null>(null);
	const [rows, setRows] = useState<ConceptRun[]>([]);
	const [elapsed, setElapsed] = useState(0);
	const [expanded, setExpanded] = useState<string | null>(null);
	const [differenceType, setDifferenceType] = useState("all");
	const [reconciliation, setReconciliation] =
		useState<ReconciliationSelection | null>(null);
	const [notice, setNotice] = useState<{
		message: string;
		success: boolean;
	} | null>(null);
	const locked = running || refreshing !== null || reconciliation !== null;
	const summaryTypes = DIFFERENCE_TYPES.slice(1).filter(
		(type) =>
			type.key !== "Barrel names" ||
			rows.some((row) => row.concept.id === "barrels"),
	);
	const active = useRef<AbortController | null>(null);
	const validDates = from?.isValid() && to?.isValid() && from.isBefore(to);

	useEffect(() => {
		const controller = new AbortController();
		setLoadError("");
		getConcepts(controller.signal)
			.then((options) => {
				if (!controller.signal.aborted) {
					setConcepts(options);
					setSelected(options);
				}
			})
			.catch((error) => {
				if (!controller.signal.aborted)
					setLoadError(
						error instanceof Error ? error.message : "Could not load concepts.",
					);
			});
		return () => controller.abort();
	}, [retry]);
	useEffect(() => () => active.current?.abort(), []);
	useEffect(() => {
		if (!running || !run) return;
		const timer = window.setInterval(
			() => setElapsed(Math.floor((Date.now() - run.started) / 1000)),
			1000,
		);
		return () => window.clearInterval(timer);
	}, [running, run]);

	async function start() {
		if (
			active.current ||
			reconciliation ||
			!validDates ||
			!from ||
			!to ||
			selected.length === 0
		)
			return;
		const controller = new AbortController();
		const frozen = {
			id: createComparisonRunId(),
			from: from.toISOString(),
			to: to.toISOString(),
			started: Date.now(),
		};
		const selection = [...selected];
		active.current = controller;
		setRun(frozen);
		setNotice(null);
		setRows(selection.map((concept) => ({ concept, status: "Queued" })));
		setRunning(true);
		setElapsed(0);
		setExpanded(null);
		setDifferenceType("all");
		try {
			for (const concept of selection) {
				if (controller.signal.aborted) break;
				setRows((current) =>
					current.map((row) =>
						row.concept.id === concept.id ? { ...row, status: "Running" } : row,
					),
				);
				try {
					const result = await compareConcept(
						concept.id,
						frozen.from,
						frozen.to,
						frozen.id,
						controller.signal,
					);
					if (controller.signal.aborted) break;
					setRows((current) =>
						current.map((row) =>
							row.concept.id === concept.id
								? { ...row, status: "Completed", result }
								: row,
						),
					);
				} catch (error) {
					if (controller.signal.aborted) break;
					setRows((current) =>
						current.map((row) =>
							row.concept.id === concept.id
								? {
										...row,
										status: "Failed",
										error:
											error instanceof Error
												? error.message
												: "Comparison failed.",
									}
								: row,
						),
					);
				}
			}
		} finally {
			setRows((current) =>
				current.map((row) =>
					row.status === "Queued" || row.status === "Running"
						? { ...row, status: "Cancelled" }
						: row,
				),
			);
			setElapsed(Math.floor((Date.now() - frozen.started) / 1000));
			setRunning(false);
			active.current = null;
		}
	}

	async function refreshCategory(conceptId: string) {
		if (active.current || !run) return;
		const concept = concepts.find((item) => item.id === conceptId);
		if (!concept) return;
		const controller = new AbortController();
		active.current = controller;
		setRefreshing(conceptId);
		setRows((current) =>
			current.map((row) =>
				row.concept.id === conceptId
					? { ...row, stale: true, status: "Running", error: undefined }
					: row,
			),
		);
		try {
			const result = await compareConcept(
				conceptId,
				run.from,
				run.to,
				run.id,
				controller.signal,
			);
			if (controller.signal.aborted) return;
			setRows((current) =>
				current.map((row) =>
					row.concept.id === conceptId
						? {
								...row,
								status: "Completed",
								stale: false,
								result,
								error: undefined,
							}
						: row,
				),
			);
		} catch (error) {
			if (controller.signal.aborted) return;
			const message =
				error instanceof Error ? error.message : "Comparison failed.";
			setRows((current) =>
				current.map((row) =>
					row.concept.id === conceptId
						? { ...row, status: "Failed", stale: true, error: message }
						: row,
				),
			);
			setNotice({
				message: `Could not refresh ${concept.name}. ${message} Use Refresh category to retry.`,
				success: false,
			});
		} finally {
			if (active.current === controller) {
				active.current = null;
				setRefreshing(null);
			}
		}
	}

	const completed = rows.filter(
		(row) => row.status === "Completed" || row.status === "Failed",
	).length;
	const failed = rows.filter(
		(row) => row.status === "Failed" || row.status === "Cancelled",
	).length;
	const current = rows.find((row) => row.status === "Running");
	const openDetails = (concept: string, type: string) => {
		setExpanded(concept);
		setDifferenceType(type);
		if (!locked && rows.find((row) => row.concept.id === concept)?.stale)
			void refreshCategory(concept);
	};
	const invalidateResults = (
		concept: string,
		message: string,
		success: boolean,
	) => {
		const affected = new Set([
			concept,
			...(concepts.find((item) => item.id === concept)?.dependentConcepts ??
				[]),
		]);
		setRows((current) =>
			current.map((row) =>
				row.result && affected.has(row.concept.id)
					? { ...row, stale: true }
					: row,
			),
		);
		setNotice({ message, success });
		if (success) void refreshCategory(concept);
	};

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h5" mb={2}>
				Rollshop vs MES
			</Typography>
			{notice && (
				<Alert severity={notice.success ? "success" : "warning"} sx={{ mb: 2 }}>
					{notice.message}
				</Alert>
			)}
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Stack gap={2}>
					{loadError && (
						<Alert
							severity="error"
							action={
								<Button onClick={() => setRetry((value) => value + 1)}>
									Retry
								</Button>
							}
						>
							{loadError}
						</Alert>
					)}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Autocomplete
							multiple
							size="small"
							sx={{ flex: 1, minWidth: 0 }}
							options={concepts}
							value={selected}
							disabled={locked}
							getOptionLabel={(option) => option.name}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							onChange={(_, values) => setSelected(values)}
							disableCloseOnSelect
							renderInput={(params) => (
								<TextField
									{...params}
									label="Concepts to compare"
									placeholder="Select concepts"
								/>
							)}
							renderOption={(props, option) => (
								<li {...props} key={option.id}>
									{option.name}
									{!option.dated ? " · All records" : ""}
								</li>
							)}
						/>
						<Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
							<Button
								size="small"
								sx={{ whiteSpace: "nowrap" }}
								disabled={locked || concepts.length === 0}
								onClick={() => setSelected(concepts)}
							>
								Select all
							</Button>
							<Button
								size="small"
								disabled={locked || selected.length === 0}
								onClick={() => setSelected([])}
							>
								Clear
							</Button>
						</Stack>
					</Box>
					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						<DateTimePicker
							label="From (inclusive)"
							value={from}
							onChange={setFrom}
							disabled={locked}
							timezone={timezone}
							format="YYYY-MM-DD HH:mm:ss"
							views={["year", "day", "hours", "minutes", "seconds"]}
							ampm={false}
							timeSteps={{ seconds: 1, minutes: 1 }}
							slotProps={{
								textField: {
									size: "small",
									fullWidth: false,
									sx: { width: 240, maxWidth: "100%" },
								},
							}}
						/>
						<DateTimePicker
							label="To (exclusive)"
							value={to}
							onChange={setTo}
							disabled={locked}
							timezone={timezone}
							format="YYYY-MM-DD HH:mm:ss"
							views={["year", "day", "hours", "minutes", "seconds"]}
							ampm={false}
							timeSteps={{ seconds: 1, minutes: 1 }}
							slotProps={{
								textField: {
									size: "small",
									fullWidth: false,
									sx: { width: 240, maxWidth: "100%" },
								},
							}}
						/>
						<Button
							size="small"
							sx={{ whiteSpace: "nowrap" }}
							disabled={locked}
							onClick={() => {
								const now = moment().tz(timezone).milliseconds(0);
								setTo(now);
								setFrom(now.clone().subtract(7, "days"));
							}}
						>
							Last 7 days
						</Button>
						<Button
							size="small"
							sx={{ whiteSpace: "nowrap" }}
							variant="contained"
							startIcon={<PlayArrow />}
							onClick={start}
							disabled={locked || !validDates || selected.length === 0}
						>
							Run comparison
						</Button>
						{running && (
							<Button
								size="small"
								color="warning"
								startIcon={<Stop />}
								onClick={() => active.current?.abort()}
							>
								Cancel
							</Button>
						)}
					</Box>
					{!validDates && (
						<Alert severity="warning">
							Enter valid dates with From earlier than To.
						</Alert>
					)}
				</Stack>
			</Paper>
			{run && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
					<Stack
						direction="row"
						justifyContent="space-between"
						gap={2}
						mb={1}
						flexWrap="wrap"
					>
						<Typography role="status">
							{refreshing
								? `Refreshing ${current?.concept.name ?? "category"}…`
								: running
									? `Comparing ${current?.concept.name ?? "selected concepts"}…`
									: failed
										? "Incomplete comparison"
										: "Comparison finished"}{" "}
							· {completed}/{rows.length} concepts · {elapsed}s
						</Typography>
						<Typography variant="body2">
							{moment(run.from).tz(timezone).format("YYYY-MM-DD HH:mm:ss z")} →{" "}
							{moment(run.to).tz(timezone).format("YYYY-MM-DD HH:mm:ss z")}
						</Typography>
					</Stack>
					<LinearProgress
						aria-label="Completed concepts"
						variant="determinate"
						value={rows.length ? (completed / rows.length) * 100 : 0}
					/>
					{failed > 0 && (
						<Alert severity="warning" sx={{ mt: 1 }}>
							{failed} concepts failed or were cancelled.
						</Alert>
					)}
				</Paper>
			)}
			{rows.length > 0 && (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small" aria-label="Comparison summary">
						<TableHead>
							<TableRow>
								{[
									"",
									"Concept / scope",
									"Rollshop rows",
									"MES rows",
									"Matched",
									...summaryTypes.map((type) =>
										type.key === "Barrel names" ? "Barrel names" : type.label,
									),
									"Status",
								].map((label, index) => (
									<TableCell
										key={`${label}:${index}`}
										sx={{ whiteSpace: "nowrap" }}
									>
										{label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row) => (
								<Fragment key={row.concept.id}>
									<TableRow>
										<TableCell>
											<IconButton
												size="small"
												disabled={!row.result}
												aria-label={`Expand ${row.concept.name}`}
												aria-expanded={expanded === row.concept.id}
												onClick={() => {
													if (expanded === row.concept.id) setExpanded(null);
													else openDetails(row.concept.id, "all");
												}}
											>
												{expanded === row.concept.id ? (
													<ExpandLess />
												) : (
													<ExpandMore />
												)}
											</IconButton>
										</TableCell>
										<TableCell>
											<Tooltip title={row.concept.notes}>
												<Typography variant="body2" fontWeight={600}>
													{row.concept.name}
												</Typography>
											</Tooltip>
											<Typography variant="caption" color="text.secondary">
												{row.concept.dated
													? "Selected interval + undated"
													: "All records"}
											</Typography>
										</TableCell>
										{(["rollshopRows", "mesRows", "matched"] as const).map(
											(field) => (
												<TableCell key={field}>
													{row.result?.[field].toLocaleString() ?? "—"}
												</TableCell>
											),
										)}
										{summaryTypes.map((type) => (
											<TableCell key={type.key}>
												{row.result &&
												(type.key !== "Barrel names" ||
													row.concept.id === "barrels") ? (
													<Button
														size="small"
														color={
															type.key === "MES more complete" ||
															type.key === "Only MES" ||
															(type.key === "Different values" &&
																row.result.actionableFields === 0 &&
																row.result.mappingIssues === 0)
																? "success"
																: "primary"
														}
														disabled={row.result[type.count] === 0}
														onClick={() =>
															openDetails(row.concept.id, type.key)
														}
													>
														{row.result[type.count].toLocaleString()}
													</Button>
												) : (
													"—"
												)}
											</TableCell>
										))}
										<TableCell>
											<ConceptStatusChip row={row} />
											{row.error && (
												<Typography
													variant="caption"
													color="error"
													display="block"
													sx={{ maxWidth: 330 }}
												>
													{row.error}
												</Typography>
											)}
										</TableCell>
									</TableRow>
									{row.result && (
										<TableRow>
											<TableCell
												colSpan={6 + summaryTypes.length}
												sx={{
													p: 0,
													borderBottom:
														expanded === row.concept.id ? undefined : 0,
												}}
											>
												<Collapse
													in={expanded === row.concept.id}
													unmountOnExit
												>
													<DifferenceDetails
														result={row.result}
														dated={row.concept.dated}
														type={differenceType}
														onTypeChange={setDifferenceType}
														disabled={locked}
														stale={row.stale ?? false}
														refreshing={refreshing === row.concept.id}
														onRefresh={() =>
															void refreshCategory(row.concept.id)
														}
														onInvalidate={(message, success) =>
															invalidateResults(
																row.concept.id,
																message,
																success,
															)
														}
														onUpdate={(type, key, operation, matchOption) => {
															if (!locked && !row.stale && row.result)
																setReconciliation({
																	result: row.result,
																	type,
																	key,
																	operation,
																	matchOption,
																});
														}}
													/>
												</Collapse>
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
			{reconciliation && (
				<ReconciliationDialog
					selection={reconciliation}
					onClose={() => setReconciliation(null)}
					onInvalidate={(message, success) =>
						invalidateResults(reconciliation.result.concept, message, success)
					}
				/>
			)}
		</Box>
	);
}
